import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import WorkOrderCard from "./TWorkCard";
import "./css/Thome.css";
import "./css/TModalOverlay.css";
import "./../managerPage/css/dropdown.css";
import { IoIosCloseCircle } from "react-icons/io";
import { IoMdCloseCircle } from "react-icons/io";
import { RiImageAddLine } from "react-icons/ri";
import {
  ChevronLeft,
  Search,
  Filter,
  Calendar,
  MapPin,
  AlertTriangle,
  Eye,
  X,
  Upload,
  Plus,
  Clock,
  DollarSign,
  Wrench,
  Users,
  FileText,
  CheckCircle,
  ClipboardCheck,
} from "lucide-react";

import {
  useUpdateRepairByIdMutation,
  useGetSchedulesByTechnicianEmailQuery,
  useGetRepairReportByIDQuery,
  useSubmitStartTimeMutation,
  useSubmitEndTimeMutation,
  useCompleteRepairReportMutation,
} from "../../slices/maintenanceApiSlice";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { maintenanceApiSlice } from "../../slices/maintenanceApiSlice";
import { createSelector } from "reselect";
import { toast } from "sonner";

// shadcn UI imports
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Separator } from "../../components/ui/separator";

const WorkOrderModal = ({ order, onClose, data = [] }) => {
  const [teamMembers, setTeamMembers] = useState(order.teamMembers || []);
  const [newMember, setNewMember] = useState("");
  // const [selectedWorkStatus, setSelectedWorkStatus] = useState("");
  const [images, setImages] = useState([]); // Allow multiple images
  const [imageError, setImageError] = useState("");
  const fileInputRef = useRef(null);
  const [selectedWorkStatus, setSelectedWorkStatus] = useState(
    order.repairInfo.status || "Pending",
  );

  const [updateRepairById, { isLoading, error }] =
    useUpdateRepairByIdMutation();

  const repairID = order.repairInfo.repairID;

  const { data: repairReport, refetch } = useGetRepairReportByIDQuery(
    repairID,
    {
      skip: !repairID, // skip until userID is available
    },
  );

  const reportExists = Array.isArray(repairReport) && repairReport.length > 0;
  const [submitStartTime] = useSubmitStartTimeMutation();
  const [submitEndTime] = useSubmitEndTimeMutation();
  const [completeRepairReport, { isLoading: posting }] =
    useCompleteRepairReportMutation();

  useEffect(() => {
    if (reportExists) {
      const report = repairReport[0];

      setFormData({
        reportID: report.repairReportID ?? "",
        startTime: report.startTime ?? "",
        endTime: report.endTime ?? "",
        finishedDate: report.finishedDate ?? "",
        totalCost: report.totalCost?.toString() ?? "",
        information: report.information?.toString() ?? "",
        partsUsed: Array.isArray(report.partsUsed)
          ? report.partsUsed.join(", ")
          : (report.partsUsed?.toString() ?? ""),
        repairID: report.repairID ?? "",
        images: report.images ?? [],
      });
    }
  }, [repairReport, reportExists]);

  const [formData, setFormData] = useState({
    reportID: "",
    startTime: "",
    endTime: "",
    finishedDate: "",
    totalCost: "",
    information: "",
    partsUsed: "",
    repairID: repairID,
    images: [],
  });

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };
  // Handle adding new team member
  const handleAddMember = () => {
    if (newMember.trim() && !teamMembers.includes(newMember)) {
      setTeamMembers([...teamMembers, newMember]);
      setNewMember(""); // Reset input
    }
  };

  // Handle removing team member
  const handleRemoveMember = (index) => {
    setTeamMembers(teamMembers.filter((_, i) => i !== index));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.warning(
        "Upload Limit Exceeded: You can upload a maximum of 5 images.",
      );
      return;
    }
    setImageError("");
    setImages((prevImages) => [...prevImages, ...files]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    // pull out what we need (reportID was set in your useEffect)
    const { reportID, totalCost, information, partsUsed } = formData;

    // basic validation
    const totalCostStr = String(totalCost || "");
    const informationStr = String(information || "");
    const partsUsedStr = String(partsUsed || "");

    if (
      !reportID ||
      !totalCostStr.trim() ||
      !informationStr.trim() ||
      !partsUsedStr.trim() ||
      teamMembers.length === 0
    ) {
      toast.warning(
        "Please fill in all fields: FinishedDate, TotalCost, Information, PartsUsed, Technicians and Images",
      );
      return;
    }

    // build FormData with ONLY the fields you still need to send
    const sendData = new FormData();
    sendData.append("totalCost", totalCostStr.trim());
    sendData.append("information", informationStr.trim());

    partsUsedStr
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean)
      .forEach((part) => sendData.append("partsUsed", part));

    teamMembers
      .map((tech) => tech.trim())
      .filter(Boolean)
      .forEach((tech) => sendData.append("technicians", tech));

    images.forEach((img) => sendData.append("images", img));

    try {
      // call your “complete” mutation
      await completeRepairReport({
        reportID, // path param
        formData: sendData, // body
      }).unwrap();

      toast.success("Repair Report completed!");

      // reset
      setFormData((f) => ({
        ...f,
        totalCost: "",
        information: "",
        partsUsed: "",
      }));
      setTeamMembers([]);
      setImages([]);
      refetch();
    } catch (err) {
      console.error("Error completing repair report:", err);
      toast.error(err?.data?.message || "Something went wrong.");
    }
  };

  const WorkOrder = [
    { value: "Pending", label: "Pending" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
  ];

  const getFilteredOptions = () => {
    switch (selectedWorkStatus) {
      case "Pending":
        return WorkOrder.filter((o) => o.value !== "Completed"); // Only allow In Progress
      case "In Progress":
        return WorkOrder.filter((o) => o.value !== "Pending"); // Only allow Completed
      case "Completed":
        return WorkOrder.filter((o) => o.value === "Completed"); // Disable changing
      default:
        return WorkOrder;
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] md:w-full max-h-[90vh] overflow-y-auto rounded-xl ">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="text-xl font-bold">
            Work Order Details
          </DialogTitle>
        </DialogHeader>

        <form className="space-y-6 pt-4" onSubmit={handleAdd}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assetName">Asset Name:</Label>
              <Input
                id="assetName"
                type="text"
                value={order.repairInfo.assetName}
                readOnly
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date">Date:</Label>
              <Input
                id="date"
                type="text"
                value={order.reportingDate || "N/A"}
                readOnly
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="area">Area:</Label>
              <Input
                id="area"
                type="text"
                value={order.repairInfo.area || "N/A"}
                readOnly
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="complainerName">Complainer Name:</Label>
              <Input
                id="complainerName"
                type="text"
                value={order.repairInfo.name || "N/A"}
                readOnly
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactNumber">Contact Number:</Label>
              <Input
                id="contactNumber"
                type="text"
                value={order.repairInfo.phoneNumber || "N/A"}
                readOnly
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description:</Label>
            <Textarea
              id="description"
              value={order.repairInfo.description || "No description"}
              readOnly
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <Label>Asset Images:</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3 sm:p-4 border rounded-lg bg-gray-50/50">
              {Array.isArray(order.repairInfo.images) &&
              order.repairInfo.images.length > 0 ? (
                order.repairInfo.images.map((imgSrc, index) => (
                  <img
                    key={index}
                    src={imgSrc}
                    alt={`Work Order ${index + 1}`}
                    className="w-full aspect-square object-cover rounded-lg border shadow-sm"
                  />
                ))
              ) : order.repairInfo.images ? (
                <img
                  src={order.repairInfo.images}
                  alt="Work Order"
                  className="w-full aspect-square object-cover rounded-lg border shadow-sm"
                />
              ) : (
                <p className="text-gray-500 text-sm py-2">No image available</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="workStatus">Work Status:</Label>
            <Select
              value={selectedWorkStatus}
              onValueChange={async (value) => {
                setSelectedWorkStatus(value);

                try {
                  await updateRepairById({
                    repairID: order.repairID,
                    updateFields: { status: value },
                  }).unwrap();

                  if (value === "In Progress") {
                    const currentTime = new Date().toLocaleTimeString("en-GB", {
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                    const response = await submitStartTime({
                      repairID: order.repairID,
                      startTime: currentTime,
                    }).unwrap();

                    setFormData((prev) => ({
                      ...prev,
                      startTime: response.startTime,
                    }));
                  }

                  if (value === "Completed") {
                    const currentTime = new Date().toLocaleTimeString("en-GB", {
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                    const currentDate = new Date().toISOString().split("T")[0];

                    const response = await submitEndTime({
                      reportID: formData.reportID,
                      endTime: currentTime,
                      finishedDate: currentDate,
                    }).unwrap();

                    setFormData((prev) => ({
                      ...prev,
                      endTime: response.endTime,
                      finishedDate: response.finishedDate,
                    }));
                  }

                  refetch();
                  toast.success(
                    `Work Status Updated: Status is now "${value}"`,
                  );
                } catch (err) {
                  console.error("❌ Failed to update work status:", err);
                  toast.error(
                    "Error Updating Status: Could not update status. Try again later.",
                  );
                }
              }}
            >
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {getFilteredOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Execution Times:</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label
                  htmlFor="startTime"
                  className="text-xs text-zinc-500 font-bold uppercase tracking-wider"
                >
                  Start Time
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  value={
                    reportExists && repairReport?.startTime
                      ? repairReport.startTime
                      : formData.startTime
                  }
                  readOnly
                  className="bg-zinc-50"
                />
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor="endTime"
                  className="text-xs text-zinc-500 font-bold uppercase tracking-wider"
                >
                  End Time
                </Label>
                <Input
                  id="endTime"
                  type="time"
                  value={
                    reportExists && repairReport?.endTime
                      ? repairReport.endTime
                      : formData.endTime
                  }
                  readOnly={reportExists}
                  className={reportExists ? "bg-zinc-50" : ""}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="finishedDate">Finished Date:</Label>
              <Input
                id="finishedDate"
                type="date"
                value={
                  reportExists && repairReport?.finishedDate
                    ? repairReport.finishedDate
                    : formData.finishedDate
                }
                readOnly={reportExists}
                className={reportExists ? "bg-zinc-50" : ""}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="totalCost">Total Cost:</Label>
              <Input
                id="totalCost"
                type="text"
                value={
                  reportExists && repairReport[0]?.totalCost
                    ? repairReport[0].totalCost
                    : formData.totalCost
                }
                onChange={(e) => handleInputChange(e, "totalCost")}
                placeholder="Enter Total Cost"
                readOnly={reportExists && !!repairReport[0]?.totalCost}
                className={
                  reportExists && !!repairReport[0]?.totalCost
                    ? "bg-zinc-50"
                    : ""
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="partsUsed">Parts Used:</Label>
            <Input
              id="partsUsed"
              type="text"
              value={
                reportExists && repairReport[0]?.partsUsed
                  ? repairReport[0].partsUsed
                  : formData.partsUsed || ""
              }
              onChange={(e) =>
                setFormData({ ...formData, partsUsed: e.target.value })
              }
              placeholder="Enter parts used (e.g., wood, metal, screws)"
              readOnly={reportExists && !!repairReport[0]?.partsUsed}
            />
          </div>
          {reportExists && repairReport[0]?.images ? (
            <div className="space-y-2">
              <Label>Repaired Images:</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 border rounded-lg bg-gray-50">
                {repairReport[0].images.map((imgSrc, index) => (
                  <img
                    key={index}
                    src={imgSrc}
                    alt={`Work Order ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border"
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Upload Images:</Label>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                multiple
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />
              {imageError && (
                <p className="text-red-500 text-sm">{imageError}</p>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 border rounded-lg bg-gray-50">
                {images.map((img, idx) => (
                  <div key={idx} className="relative">
                    <img
                      src={
                        typeof img === "string" ? img : URL.createObjectURL(img)
                      }
                      alt={`Preview ${idx}`}
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                      onClick={() => removeImage(idx)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {images.length < 5 && (
                  <div
                    className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <RiImageAddLine className="w-8 h-8 text-gray-400" />
                    <p className="text-gray-500 text-sm mt-2">Upload Images</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {reportExists && repairReport[0]?.technicians ? (
            <div className="space-y-2">
              <Label>Team Members:</Label>
              <div className="flex flex-wrap gap-2 p-4 border rounded-lg bg-gray-50">
                {repairReport[0].technicians.split(",").map((member, index) => (
                  <Badge key={index} variant="secondary">
                    {member}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="newMember">Team Members:</Label>
                <div className="flex gap-2">
                  <Input
                    id="newMember"
                    type="text"
                    placeholder="Enter Name"
                    value={newMember}
                    onChange={(e) => setNewMember(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleAddMember}
                    variant="outline"
                    style={{
                      backgroundColor:
                        "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                      color: "white",
                    }}
                  >
                    Add
                  </Button>
                </div>
              </div>
              {teamMembers.length > 0 && (
                <div className="space-y-2">
                  <Label></Label>
                  <div className="flex flex-wrap gap-2 p-4 border rounded-lg bg-gray-50">
                    {teamMembers.map((member, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        {member}
                        <IoMdCloseCircle
                          onClick={() => handleRemoveMember(index)}
                          className="w-4 h-4 cursor-pointer hover:text-red-500"
                        />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="additionalInfo">Additional Information:</Label>
            <Textarea
              id="additionalInfo"
              value={
                reportExists && repairReport[0]?.information
                  ? repairReport[0].information
                  : formData.information
              }
              onChange={(e) => handleInputChange(e, "information")}
              placeholder="Enter any additional information"
              readOnly={reportExists && !!repairReport[0]?.information}
              className="min-h-[100px]"
            />
          </div>

          {(!reportExists ||
            !repairReport[0]?.finishedDate ||
            !repairReport[0]?.totalCost ||
            !repairReport[0]?.information ||
            !repairReport[0]?.partsUsed ||
            !repairReport[0]?.images?.length) && (
            <DialogFooter>
              <Button type="submit" disabled={posting}>
                {posting ? "Posting..." : "Done"}
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

const WorkOrderList = ({
  searchTerm,
  setSearchTerm,
  selectedPriority,
  setSelectedPriority,
  uniquePriorities,
  filteredData,
  setSelectedOrder,
}) => {
  return (
    <Card className="border-zinc-200 shadow-sm overflow-hidden bg-zinc-50/30">
      <CardHeader className="bg-white border-b px-6 py-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              placeholder="Search by asset, area, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 border-zinc-200 bg-zinc-50/50 focus-visible:ring-zinc-900 border-none shadow-inner"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            <div className="flex-1 sm:w-48">
              <Select
                value={selectedPriority}
                onValueChange={(value) => setSelectedPriority(value)}
              >
                <SelectTrigger className="h-10 border-zinc-200 bg-white">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-zinc-400" />
                    <SelectValue placeholder="All Priorities" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {uniquePriorities.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredData.map((order) => (
            <WorkOrderCard
              key={order.scheduleID}
              {...order}
              onView={() => setSelectedOrder(order)}
            />
          ))}
        </div>

        {filteredData.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="h-16 w-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
              <ClipboardCheck className="h-8 w-8 text-zinc-300" />
            </div>
            <h3 className="text-lg font-bold text-zinc-900">
              No Work Orders Found
            </h3>
            <p className="text-zinc-500 max-w-[250px] mx-auto mt-1">
              We couldn't find any work orders matching your current filters.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TechnicianWorkOrder = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedWorkStatus, setSelectedWorkStatus] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const status = searchParams.get("status");
    if (status) {
      setSelectedWorkStatus(status);
    } else {
      setSelectedWorkStatus("");
    }
  }, [searchParams]);

  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(selectUserInfo, (userInfo) => {
    const email = userInfo?.username || userInfo?.email || "";
    return email === "null" ? "" : email;
  });
  const [data, setData] = useState([]);
  const email = useSelector(getUserEmail);
  const { data: userData } = useGetUserByEmailQuery(email, {
    skip: !email,
  });

  const userEmail = userData?.user?.email || email;
  const { data: technicianSchedules, isLoading } =
    useGetSchedulesByTechnicianEmailQuery(userEmail, {
      skip: !userEmail,
    });
  const dispatch = useDispatch();

  useEffect(() => {
    if (isLoading) {
      toast.loading("Loading work order...", { id: "loading-work-orders" });
    } else {
      toast.dismiss("loading-work-orders");
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading && (!technicianSchedules || technicianSchedules.length === 0)) {
      toast.info("No Work Orders: You currently have no assigned work orders.");
    }
  }, [isLoading, technicianSchedules]);

  useEffect(() => {
    const fetchRepairDetails = async () => {
      if (technicianSchedules && technicianSchedules.length) {
        const repairPromises = technicianSchedules.map(async (schedule) => {
          try {
            const repair = await dispatch(
              maintenanceApiSlice.endpoints.getRepairById.initiate(
                schedule?.repairID,
                {
                  forceRefetch: true,
                },
              ),
            ).unwrap();

            return {
              ...schedule,
              repairInfo: repair, // attach the repair detail here
            };
          } catch (err) {
            console.error(
              `❌ Error fetching repair for ID ${schedule?.repairID}:`,
              err,
            );
            return null; // return null to filter out later
          }
        });

        const results = await Promise.all(repairPromises);
        const validMergedData = results.filter(Boolean); // filter out failed ones
        setData(validMergedData); // ⬅️ final combined array
      }
    };

    fetchRepairDetails();
  }, [technicianSchedules, dispatch]);

  const uniquePriorities = [
    { value: "all", label: "All Priorities" },
    ...Array.from(
      new Set(
        data
          .map((item) => item.repairInfo.priority?.toLowerCase())
          .filter(Boolean),
      ),
    ).map((priority) => ({
      value: priority,
      label: priority
        ? priority.charAt(0).toUpperCase() + priority.slice(1)
        : "",
    })),
  ];

  // Filtering data based on search and priority selection and work status
  // Helper function to recursively search through nested objects
  const searchRecursively = (obj, searchTerm) => {
    if (typeof obj !== "object" || obj === null) {
      return (
        obj &&
        obj
          .toString()
          .toLowerCase()
          .includes(searchTerm?.toLowerCase() || "")
      );
    }

    // Check all values in the object (including nested objects)
    return Object.values(obj).some((value) =>
      searchRecursively(value, searchTerm),
    );
  };

  const filteredData =
    data && data.length
      ? data.filter((item) => {
          // Match search term with any field at any level in the object
          const matchesSearch = searchRecursively(item, searchTerm);

          const matchesPriority =
            selectedPriority === "all" ||
            item.repairInfo.priority?.toLowerCase() ===
              selectedPriority.toLowerCase();

          const matchesWorkStatus =
            selectedWorkStatus === "" ||
            item.repairInfo.status?.toLowerCase() ===
              selectedWorkStatus.toLowerCase();

          return matchesSearch && matchesPriority && matchesWorkStatus;
        })
      : [];

  return (
    <div className="p-4 sm:py-6 sm:px-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-1">
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-zinc-900 tracking-tight flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.history.back()}
                className="h-9 w-9 rounded-full sm:hidden"
                style={{
                  backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                  color: "white",
                }}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              Work Orders
            </h1>
            <p className="text-sm font-medium text-zinc-500">
              View and manage assigned maintenance tasks
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="h-8 px-3 rounded-full border-zinc-200 bg-white font-bold text-zinc-600"
            >
              {filteredData.length} Total
            </Badge>
          </div>
        </div>

        <div className="mt-6">
          <WorkOrderList
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedPriority={selectedPriority}
            setSelectedPriority={setSelectedPriority}
            uniquePriorities={uniquePriorities}
            filteredData={filteredData}
            setSelectedOrder={setSelectedOrder}
          />
        </div>

        {selectedOrder && (
          <WorkOrderModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            data={data}
          />
        )}
      </div>
    </div>
  );
};

export default TechnicianWorkOrder;
